import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Tooltip from '@mui/material/Tooltip';
import DashboardCard, { EmptyState } from './DashboardCard';
import CardGrid from './CardGrid';
import type { CardColumn } from './cardColumns';
import type { DashboardSelectiveMenu } from '../../types/dashboard';
import { useGlobalStore } from '../../stores/globalStore';
import AimSelectiveMenuDialog from '../people/AimSelectiveMenuDialog';
import { useAimSelectiveMenuHistorical, type AimSMRunResponse } from '../../api/aimSelectiveMenuApi';
import { useDashboardNav, RESIDENT_TAB } from './useDashboardNav';
import { useStagedPermission } from '../../hooks/usePermissionEnforcement';

interface Props {
  data: DashboardSelectiveMenu[];
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

/** Auto Process Date (newest first, with its time), Name, Change Type. */
const columns: CardColumn<DashboardSelectiveMenu>[] = [
  { dataField: 'autoProcessedDateTime', caption: 'Auto Process Date', dataType: 'datetime', format: 'M/d/yyyy h:mm a', sortOrder: 'desc' },
  { dataField: 'fullName', caption: 'Name', sizing: 'fill' },
  { dataField: 'transactionName', caption: 'Change Type' },
];

// Row-level accessor helpers — grid rows arrive with the same
// FirstCharLowerPolicy-cased keys as the rest of the API. Only
// need a small subset here for the double-click handler.
function readGuid(row: unknown): string | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const v = r.guid ?? r.GUID ?? r.Guid;
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function readPatientId(row: unknown): number | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const v = r.fKey_Patient ?? r.FKey_Patient;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
function readFullName(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const r = row as Record<string, unknown>;
  const v = r.fullName ?? r.FullName;
  return typeof v === 'string' ? v : '';
}

export default function SelectiveMenuAutoCard({ data, ...dragProps }: Props) {
  const { openResident } = useDashboardNav();
  const breakfastName = useGlobalStore((s) => s.breakfastName);
  const lunchName = useGlobalStore((s) => s.lunchName);
  const dinnerName = useGlobalStore((s) => s.dinnerName);
  const facilityId = useGlobalStore((s) => s.facilityId);

  // Mealtime int values: 0=Breakfast, 1=Lunch, 2=Dinner (per
  // ucDashboard_SelectiveAutoGenerated.vb:32-34, i=0 to 2).
  const mealtimeLabels: Record<number, string> = useMemo(() => ({
    0: breakfastName,
    1: lunchName,
    2: dinnerName,
  }), [breakfastName, lunchName, dinnerName]);

  // Dialog state — one open dialog at a time. `selection` holds the
  // pinpoint (guid + patient) the API needs; `result` is the loaded
  // 4-table shape; `loading` gates the dialog's spinner state until
  // the historical fetch resolves. Mirrors the useSmAimChain pattern
  // ResidentDetail / DietHistoryTab use for the live path.
  const historicalMutation = useAimSelectiveMenuHistorical();
  const [selection, setSelection] = useState<{ guid: string; patientId: number; fullName: string } | null>(null);
  const [result, setResult] = useState<AimSMRunResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // The API stages the review at Selective Menu level 1 (AimSelectiveMenuController.RunHistorical) and
  // its save at 2 (ResolveDashboard). Enforce mode only: below 1 the rows do not open, below 2 the
  // dialog opens read-only.
  const staged = useStagedPermission();
  const canOpenReview = staged.allows('dietech_Selective_Menu_Level', 1);
  const canSaveReview = staged.allows('dietech_Selective_Menu_Level', 2);

  const openDialog = async (row: unknown) => {
    if (!canOpenReview) return;
    const guid = readGuid(row);
    const patientId = readPatientId(row);
    if (!guid || !patientId || !facilityId) return;
    setSelection({ guid, patientId, fullName: readFullName(row) });
    setResult(null);
    setLoading(true);
    try {
      const res = await historicalMutation.mutateAsync({ facilityId, patientId, guid });
      setResult(res);
    } catch (err) {
      // Fetch failed — close the dialog and log; user can retry
      // by double-clicking again.
      // eslint-disable-next-line no-console
      console.error('SM historical fetch failed', err);
      setSelection(null);
    } finally {
      setLoading(false);
    }
  };

  const queryClient = useQueryClient();
  const closeDialog = () => {
    setSelection(null);
    setResult(null);
    // If the user saved (or reverted), the underlying auto-processed
    // + SM log tables changed — invalidate the whole dashboard data
    // slice so THIS card and the diet-order/allergy cards all
    // refetch. `queryKey` prefix match invalidates every hours/
    // facility variant so we don't have to thread selectedHours in.
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'data'] });
  };

  // Read-only is dashboard-wide when every affected day/mealtime is
  // locked. Mirrors WinForms
  // ucDashboard_SelectiveAutoGenerated.vb:405 —
  //   `dtDayMealtime.Select("ShowLock = FALSE").Count = 0`.
  const isReadOnly = useMemo(() => {
    if (!canSaveReview) return true;
    if (!result || result.dayMealtimes.length === 0) return true;
    return result.dayMealtimes.every((r) => {
      const v = (r as Record<string, unknown>).showLock ?? (r as Record<string, unknown>).ShowLock;
      return v === true || v === 1 || v === '1' || v === 'true';
    });
  }, [result, canSaveReview]);

  const renderDetail = (props: { data: DashboardSelectiveMenu }) => {
    // Detail sort: Menu Date desc primary, Mealtime asc secondary (by
    // numeric value 0/1/2, NOT the localized label — sorting alphabetically
    // on "Breakfast/Lunch/Dinner" gives the wrong order).
    const items = [...(props.data.selectiveMenuItems ?? [])].sort((a, b) => {
      const ad = a.menuDate ? new Date(a.menuDate).getTime() : 0;
      const bd = b.menuDate ? new Date(b.menuDate).getTime() : 0;
      if (bd !== ad) return bd - ad;
      return (a.mealtime ?? 0) - (b.mealtime ?? 0);
    });
    // Double-click on a detail row opens the same viewer as a
    // master row double-click — WinForms wires both `gv.DoubleClick`
    // and `gvDetails.DoubleClick` to the same handler (.vb:79).
    return (
      <div className="sm-detail-inline">
        <div className="sm-detail-header">
          {props.data.fullName} · {items.length} affected day/mealtime{items.length === 1 ? '' : 's'}
        </div>
        <table className="sm-detail-table">
          <thead>
            <tr>
              <th>Menu Date</th>
              <th>Day</th>
              <th>Mealtime</th>
              <th>Menu Name</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} onDoubleClick={() => openDialog(props.data)} style={{ cursor: 'pointer' }}>
                <td>{item.menuDate ? new Date(item.menuDate).toLocaleDateString() : ''}</td>
                <td>{item.day}</td>
                <td>{mealtimeLabels[item.mealtime] ?? item.mealtime}</td>
                <td>{item.menu_Name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const infoIcon = (
    <Tooltip title="Double click to view selective menu changes." arrow>
      <i
        className="bi bi-info-circle"
        style={{
          color: 'var(--text-secondary, #64748b)',
          fontSize: 14,
          cursor: 'help',
        }}
      />
    </Tooltip>
  );

  return (
    <>
      <DashboardCard icon="bi-list-check" title="Auto Generated Selective Menus" count={data.length} headerRight={infoIcon} {...dragProps}>
        {data.length === 0 ? (
          <EmptyState message="No auto-generated menus" />
        ) : (
          <div style={{ height: '100%', width: '100%' }}>
            {/* A click opens the resident's Selective Menu section; a
                double-click (or Enter on the keyboard row) opens the
                change viewer, as WinForms' gv.DoubleClick does. */}
            <CardGrid<DashboardSelectiveMenu>
              ariaLabel="Auto Generated Selective Menus"
              dataSource={data}
              keyExpr="guid"
              columns={columns}
              height="100%"
              sorting={{ mode: 'single' }}
              focusedRowEnabled
              className="card-grid-link"
              onRowClick={(e) => openResident(e.data?.fKey_Patient, RESIDENT_TAB.selectiveMenu)}
              onRowDblClick={(e) => { void openDialog(e.data); }}
              masterDetail={{ enabled: true, autoExpandAll: false, render: renderDetail }}
            />
          </div>
        )}
      </DashboardCard>

      {/* Selective Menu Changes viewer — reuses the same dialog
          the patient-profile diet-order AIM flow opens. Dashboard
          mode disables the 2-tier revert Cancel prompt (nothing
          was pre-deleted on the historical path) and — when every
          affected day/mealtime is locked — collapses to a fully
          read-only view. When SOME rows are unlocked, per-mealtime
          lock icon on the New-column header AND per-mealtime Push
          disable communicate which proposals can still be applied.
          Save-back for unlocked rows is deferred to phase 2. */}
      {selection && facilityId && (
        <AimSelectiveMenuDialog
          open
          facilityId={facilityId}
          patientId={selection.patientId}
          patientFullName={selection.fullName}
          result={result}
          loading={loading}
          isDashboard
          isReadOnly={isReadOnly}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
